import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { HiXMark, HiChevronDown, HiChevronUp, HiHeart, HiOutlineHeart, HiBell, HiOutlineBell, HiInformationCircle, HiExclamationTriangle } from 'react-icons/hi2';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import { getProductsForStockByLocal, createProductStock,getProductStockByIdAndStorage } from '../../../../../../../../crud/management_api/stock_management';
import { getAllUnidadesMedida } from '../../../../../../../../crud/configuration_api/unidades_medida';
import { getStorageUnitsByLocalId } from '../../../../../../../../crud/management_api/storage_units';
import './CreateStock.scss';
import SearchDropdown from '../../../../../../../../components/common/SearchDropdown/SearchDropdown';

// Se asume que el tipo ProductStock está definido en ../Types
import { ProductStock } from '../../../../types';

interface CreateStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  setProductStockList?: React.Dispatch<React.SetStateAction<ProductStock[]>>;
  originalProducts?: ProductStock[];
  setOriginalProducts?: (data: ProductStock[]) => void;
  // Optional props for ingredient-specific stock creation
  ingredientId?: number;
  ingredientName?: string;
  unidadMedidaId?: number; // Pre-selected measurement unit
  onCreate?: (newStock: any) => void; // Callback when stock is created

}

const CreateStockModal: React.FC<CreateStockModalProps> = ({
  isOpen,
  onClose,
  setProductStockList,
  originalProducts,
  setOriginalProducts,
  ingredientId,
  ingredientName,
  unidadMedidaId: preselectedUnidadMedidaId,
  onCreate
}) => {
  // Lista de productos disponibles para agregar stock.
  const [entityList, setEntityList] = useState<ProductStock[]>([]);
  // Estados para StorageUnits y MeasurementUnits
  const [storageUnits, setStorageUnits] = useState<{ id: number; name: string }[]>([]);
  const [measurementUnits, setMeasurementUnits] = useState<{ id: number; name: string }[]>([]);
  // Control de carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado para manejar stock existente
  const [existingStock, setExistingStock] = useState<any>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  // Producto seleccionado (guardamos su product_id, que es un string)
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  // Datos del formulario (sin fecha)
  const [formData, setFormData] = useState({
    storage_unit_id: '',
    unidad_medida_id: '',
    system_stock: '',
    min_quantity: '',
    sensitivity: 0,
    is_following: false,
    is_favorite: false,
  });

  // Estado para controlar la visibilidad de la sección de información
  const [showInformation, setShowInformation] = useState(false);
  
  // Estado para controlar la visibilidad de las opciones avanzadas
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Set selected entity when ingredientId is provided
  useEffect(() => {
    if (isOpen && ingredientId && ingredientName) {
      const productId = `in-${ingredientId}`;
      setSelectedEntityId(productId);
      console.log('Pre-selected ingredient - productId:', productId, 'ingredientName:', ingredientName);
    }
  }, [isOpen, ingredientId, ingredientName]);

  // Función para verificar si ya existe stock con la combinación producto-almacenamiento
  const checkForExistingStock = async (productId: string, storageUnitId: string) => {
    if (!productId || !storageUnitId) return;
    
    setCheckingDuplicate(true);
    setError(null);
    
    try {
      // Extraer el ingredient_id del product_id (formato: "in-123")
      const ingredientId = productId.startsWith('in-') ? productId.substring(3) : productId;
      
      const response = await getProductStockByIdAndStorage(Number(ingredientId), Number(storageUnitId));
      
      if (response && response.data) {
        // Si encontramos stock existente, mostramos la información
        setExistingStock(response.data);
        // No establecemos error para mantener el formulario visible
      } else {
        // No existe stock duplicado
        setExistingStock(null);
      }
    } catch (error) {
      console.log('No existe stock duplicado - se puede crear');
      setExistingStock(null);
      // No mostramos error ya que esto significa que no hay duplicado
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Actualiza los campos del formulario cuando se ingresan datos
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  // Maneja el cambio del slider de sensibilidad
  const handleSensitivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setFormData((prev) => ({ ...prev, sensitivity: value }));
  };

  // useEffect para verificar duplicados cuando se selecciona producto y almacenamiento
  useEffect(() => {
    if (selectedEntityId && formData.storage_unit_id) {
      checkForExistingStock(selectedEntityId, formData.storage_unit_id);
    } else {
      setExistingStock(null);
    }
  }, [selectedEntityId, formData.storage_unit_id]);

  // Limpiar campos al cerrar el modal
  useEffect(() => {
    if (!isOpen) {
      setSelectedEntityId(null);
      setFormData({
        storage_unit_id: '',
        unidad_medida_id: '',
        system_stock: '',
        min_quantity: '',
        sensitivity: 0,
        is_following: false,
        is_favorite: false,
      });
      setError(null);
      setExistingStock(null);
      setCheckingDuplicate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user?.local_id ? Number(user.local_id) : 0;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Only fetch products list if not pre-selecting an ingredient (optimization)
        if (!ingredientId) {
          const productsRes = await getProductsForStockByLocal(localId);
          setEntityList(productsRes.data || []);
        }
        
        // Cargar StorageUnits
        const storageRes = await getStorageUnitsByLocalId(localId);
        const mappedStorage = storageRes.data.map((unit: any) => ({
          id: unit.storage_unit_id,
          name: unit.name,
        }));
        setStorageUnits(mappedStorage);
        
        // Cargar MeasurementUnits
        const measureRes = await getAllUnidadesMedida();
        const mappedMeasurements = measureRes.data.map((mu: any) => ({
          id: mu.unidad_medida_id,
          name: mu.name,
        }));
        setMeasurementUnits(mappedMeasurements);

        // Set pre-selected measurement unit if provided, otherwise set default
        if (preselectedUnidadMedidaId) {
          setFormData((prev) => ({ ...prev, unidad_medida_id: String(preselectedUnidadMedidaId) }));
          console.log('Pre-selected unidad_medida_id:', preselectedUnidadMedidaId);
        } else if (!formData.unidad_medida_id) {
          const defaultUnit = mappedMeasurements.find(
            (mu: { id: number; name: string; }) => mu.id === 5 || mu.name === 'Unidad'
          );
          if (defaultUnit) {
            setFormData((prev) => ({ ...prev, unidad_medida_id: String(defaultUnit.id) }));
          }
        }
      } catch (err) {
        console.error('Error al obtener datos: ', err);
        setError('Error al obtener los datos. Por favor, intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ingredientId, preselectedUnidadMedidaId]);

  // Función para manejar la selección de un producto
  const handleSelectEntity = (entity: ProductStock) => {
    setSelectedEntityId(entity.product_id); // Seleccionar el producto
    setFormData((prev) => ({
      ...prev,
      system_stock: '', // Limpiar el campo de stock
      min_quantity: '', // Limpiar el campo de stock mínimo
    }));
    // Reset duplicate check states when selecting a new product
    setExistingStock(null);
  };


  // Manejo de guardado
  const handleSave = async () => {
    // Verificar si hay stock duplicado
    if (existingStock) {
      toast.error('No se puede crear el stock. Ya existe stock para este producto en el almacenamiento seleccionado. Por favor, selecciona otro almacenamiento.');
      return;
    }

    // Validación estricta de campos
    if (
      !formData.storage_unit_id ||
      !formData.unidad_medida_id ||
      !selectedEntityId ||
      formData.system_stock === '' ||
      formData.min_quantity === ''
    ) {
      setError('Los campos obligatorios deben ser completados.');
      return;
    }
    // Validar que los valores sean numéricos y positivos
    const systemStockNum = Number(formData.system_stock);
    const minQuantityNum = Number(formData.min_quantity);
    const storageUnitNum = Number(formData.storage_unit_id);
    const unidadMedidaNum = Number(formData.unidad_medida_id);
    if (
      isNaN(systemStockNum) || systemStockNum < 0 ||
      isNaN(minQuantityNum) || minQuantityNum < 0 ||
      isNaN(storageUnitNum) || storageUnitNum <= 0 ||
      isNaN(unidadMedidaNum) || unidadMedidaNum <= 0
    ) {
      setError('Ingrese valores válidos y positivos en todos los campos numéricos.');
      return;
    }
    // Crear payload para producto
    const payload = {
      product_id: selectedEntityId,
      storage_unit_id: storageUnitNum,
      unidad_medida_id: unidadMedidaNum,
      system_stock: systemStockNum,
      reported_stock: systemStockNum,
      min_quantity: minQuantityNum,
      sensitivity: formData.sensitivity,
      is_following: formData.is_following,
      is_favorite: formData.is_favorite,
    };
    console.log('Payload creado:', payload);
    try {
      console.log('Enviando a backend:', payload);
      createProductStock(payload)
        .then((response) => {
          console.log('Respuesta backend:', response);
          if (response && (response as any).data) {
            toast.success('Stock creado exitosamente');
            
            // Ensure we have all the necessary data from the response
            const backendStock = (response as any).data;
            
            // Get the selected items for proper naming
            const selectedProduct = entityList.find((entity) => entity.product_id === selectedEntityId);
            const selectedStorageUnit = storageUnits.find((su) => su.id === storageUnitNum);
            const selectedMeasurementUnit = measurementUnits.find((mu) => mu.id === unidadMedidaNum);
            
            // Create a properly structured ProductStock object with all fields
            const updatedStock: ProductStock = {
              ...backendStock,
              // Ensure these fields are included from our form data if not in response
              min_quantity: backendStock.min_quantity || minQuantityNum,
              sensitivity: backendStock.sensitivity !== undefined ? backendStock.sensitivity : formData.sensitivity,
              is_favorite: backendStock.is_favorite !== undefined ? backendStock.is_favorite : formData.is_favorite,
              is_following: backendStock.is_following !== undefined ? backendStock.is_following : formData.is_following,
              // Ensure other important fields are present
              product_id: backendStock.product_id || selectedEntityId,
              storage_unit_id: backendStock.storage_unit_id || storageUnitNum,
              unidad_medida_id: backendStock.unidad_medida_id || unidadMedidaNum,
              system_stock: backendStock.system_stock || systemStockNum,
              reported_stock: backendStock.reported_stock || systemStockNum,
              // Ensure proper naming from our selected items
              name: backendStock.name || selectedProduct?.name || ingredientName || 'Producto',
              storage_unit_name: backendStock.storage_unit_name || selectedStorageUnit?.name || 'Almacenamiento',
              unidad_medida_name: backendStock.unidad_medida_name || selectedMeasurementUnit?.name || 'Unidad',
              acronym: backendStock.acronym || selectedMeasurementUnit?.name?.substring(0, 3).toUpperCase() || 'UNI',
            };
            
            console.log('Stock estructurado para frontend:', updatedStock);
            
            // If onCreate callback is provided (ingredient-specific creation), use it
            if (onCreate) {
              onCreate(updatedStock);
            } else if (setProductStockList && setOriginalProducts && originalProducts) {
              // Original behavior for general stock list
              const newOriginalStock = [updatedStock, ...originalProducts];
              setProductStockList(newOriginalStock);
              setOriginalProducts(newOriginalStock);
            }
            
            onClose();
          }
        })
        .catch((error) => {
          console.error('Error al crear stock:', error);
          let backendMsg = 'Ocurrió un error al crear el stock. Verifique los datos e intente nuevamente.';
          if (error.response) {
            console.log('Error response:', error.response);
            if (error.response.data && error.response.data.message) {
              backendMsg = error.response.data.message;
            }
          }
          setError(backendMsg);
          toast.error(backendMsg);
        });
    } catch (error) {
      console.error('Error al crear stock:', error);
      setError('Ocurrió un error al crear el stock.');
      toast.error('Ocurrió un error al crear el stock.');
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered dialogClassName="mp-modal" size="lg">
      <Modal.Header className="mp-modal-header">
        <div className="form-modal-title-container">
          <span className="form-modal-title">
            {ingredientId ? 'Agregar Stock de Ingrediente' : 'Crear stock de Producto'}
          </span>
        </div>
        <button className="mp-modal-close" onClick={onClose}>
          <HiXMark size={20} />
        </button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Sección de Información */}
        <div className="create-stock-info-section">
          <button
            type="button"
            className="create-stock-info-toggle"
            onClick={() => setShowInformation(!showInformation)}
          >
            <HiInformationCircle size={18} /> Información {!showInformation ? <HiChevronDown /> : <HiChevronUp />}
          </button>
          {showInformation && (
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden', marginTop: '0.75rem' }}>
              <div style={{ padding: '1.25rem' }}>
                <h5 style={{ color: 'var(--mediopunto-orange)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>¿Qué es un stock?</h5>
                <p style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                  El stock se refiere a la cantidad de items/ingredientes/utensilios disponibles en el inventario de tu local.
                </p>
                <h5 style={{ color: 'var(--mediopunto-orange)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Descripción de campos:</h5>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginBottom: 0, fontSize: '0.875rem', lineHeight: 1.5 }}>
                  <li><strong style={{ color: 'var(--mediopunto-orange)' }}>Producto:</strong> Elija el producto a gestionar en el stock.</li>
                  <li><strong style={{ color: 'var(--mediopunto-orange)' }}>Almacenamiento:</strong> Tipo de empaque o presentación en inventario.</li>
                  <li><strong style={{ color: 'var(--mediopunto-orange)' }}>Unidad de Medida:</strong> Forma en la que se cuantifica el producto (ej. kg, lt, unidades).</li>
                  <li><strong style={{ color: 'var(--mediopunto-orange)' }}>Stock (Cantidad):</strong> Número de unidades disponibles en inventario.</li>
                  <li><strong style={{ color: 'var(--mediopunto-orange)' }}>Stock Mínimo:</strong> Cantidad mínima que debe mantenerse en inventario antes de requerir reposición.</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {loading ? (
            <MiniLoadingSpinner left='50%'/>
        ) : (
          <>
            {/* Mostrar error general si existe (no relacionado con duplicados) */}
            {error && (
              <div className="mp-form-hint" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#ef4444', marginBottom: '1rem' }}>
                <HiExclamationTriangle size={18} style={{ flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}
            
            {/* Form Fields */}
            <div className="mp-form-group create-stock-row">
              <label className="mp-form-label create-stock-row-label">Producto</label>
              <div className="create-stock-row-field">
                {ingredientId && ingredientName ? (
                  <input
                    type="text"
                    className="mp-form-input"
                    value={ingredientName}
                    disabled
                    readOnly
                    style={{ fontWeight: 600, cursor: 'not-allowed' }}
                  />
                ) : (
                  <SearchDropdown
                    items={entityList}
                    selected={entityList.find((entity) => entity.product_id === selectedEntityId) || null}
                    setSelected={handleSelectEntity}
                    displayProperty="name"
                    idProperty="product_id"
                    placeholder="Buscar un producto..."
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            <div className="mp-form-group create-stock-row">
              <label className="mp-form-label create-stock-row-label">Almacenamiento</label>
              <div className="create-stock-row-field">
                <SearchDropdown
                  items={storageUnits}
                  selected={storageUnits.find((su) => String(su.id) === formData.storage_unit_id) || null}
                  setSelected={(su) => setFormData((prev) => ({ ...prev, storage_unit_id: String(su.id) }))}
                  displayProperty="name"
                  idProperty="id"
                  placeholder="Seleccione una opción"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Indicador de verificación de duplicados */}
            {checkingDuplicate && !existingStock && (
              <div className="mp-form-hint" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '1rem' }}>
                <HiInformationCircle size={18} style={{ flexShrink: 0, color: '#3b82f6' }} />
                <span>Verificando si ya existe stock para esta combinación...</span>
              </div>
            )}

            {/* Mostrar información del stock existente */}
            {existingStock && (
              <div className="mp-form-hint" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#eab308', marginBottom: '1rem' }}>
                <HiExclamationTriangle size={18} style={{ flexShrink: 0 }} />
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Stock Existente Encontrado</h6>
                  <p style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}>
                    <strong>Producto:</strong> {existingStock.name}<br/>
                    <strong>Almacenamiento:</strong> {existingStock.storage_unit_name}<br/>
                    <strong>Stock Actual:</strong> {existingStock.system_stock} {existingStock.acronym}<br/>
                    <strong>Stock Reportado:</strong> {existingStock.reported_stock} {existingStock.acronym}
                  </p>
                  <small style={{ fontStyle: 'italic' }}>Para crear stock para este producto, por favor selecciona otro almacenamiento.</small>
                </div>
              </div>
            )}

            <div className="mp-form-group create-stock-row">
              <label className="mp-form-label create-stock-row-label">Medida</label>
              <div className="create-stock-row-field">
                {preselectedUnidadMedidaId ? (
                  <input
                    type="text"
                    className="mp-form-input"
                    value={measurementUnits.find((mu) => mu.id === preselectedUnidadMedidaId)?.name || 'Unidad'}
                    disabled
                    readOnly
                    style={{ fontWeight: 600, cursor: 'not-allowed' }}
                  />
                ) : (
                  <SearchDropdown
                    items={measurementUnits}
                    selected={measurementUnits.find((mu) => String(mu.id) === formData.unidad_medida_id) || null}
                    setSelected={(mu) => setFormData((prev) => ({ ...prev, unidad_medida_id: String(mu.id) }))}
                    displayProperty="name"
                    idProperty="id"
                    placeholder="Seleccione una opción"
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            <div className="mp-form-group create-stock-row">
              <label className="mp-form-label create-stock-row-label">Cantidad (n°)</label>
              <div className="create-stock-row-field">
                <input
                  type="number"
                  className="mp-form-input"
                  name="system_stock"
                  min={0}
                  value={formData.system_stock}
                  onChange={handleInputChange}
                  placeholder="Ingrese la cantidad"
                  onKeyDown={(e) => {
                    if (e.key === '-') e.preventDefault();
                  }}
                />
              </div>
            </div>

            <div className="mp-form-group create-stock-row">
              <label className="mp-form-label create-stock-row-label">Stock Mínimo</label>
              <div className="create-stock-row-field">
                <input
                  type="number"
                  className="mp-form-input"
                  name="min_quantity"
                  min={0}
                  value={formData.min_quantity}
                  onChange={handleInputChange}
                  placeholder="Cantidad mínima requerida"
                  onKeyDown={(e) => {
                    if (e.key === '-') e.preventDefault();
                  }}
                />
              </div>
            </div>

            {/* Advanced Options - Collapsible Section */}
            <div className="mp-form-group">
              <button
                type="button"
                className="create-stock-advanced-toggle"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                Opciones Avanzadas {!showAdvancedOptions ? <HiChevronDown /> : <HiChevronUp />}
              </button>
              
              {showAdvancedOptions && (
                <div className="create-stock-advanced-content">
                  {/* Sensitivity Slider */}
                  <div className="create-stock-option-row">
                    <label className="mp-form-label">Sensibilidad ({formData.sensitivity}%)</label>
                    <div className="create-stock-sensitivity-explanation">
                      La sensitividad es que tanto estamos dispuesto a perdonar la merma de un producto, por ejemplo, si trabajamos con harina, sabemos que de una producción de 10kg, podemos perder 100/200 gramos en el proceso, pero no sería normal que perdiéramos 1kg. Para fijar ese límite, usamos la sensibilidad.
                    </div>
                    <div className="create-stock-sensitivity-container">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={formData.sensitivity}
                        onChange={handleSensitivityChange}
                        className="create-stock-sensitivity-slider"
                      />
                      <small style={{ fontSize: '0.75rem', color: 'var(--text-color-muted)', fontStyle: 'italic' }}>
                        Nivel de alertas por stock bajo (0-100%)
                      </small>
                    </div>
                  </div>

                  {/* Following Option */}
                  <div className="create-stock-option-row">
                    <div className="create-stock-option-control">
                      <button
                        type="button"
                        className={`mp-form-chip ${formData.is_following ? 'selected' : ''}`}
                        onClick={() => setFormData((prev) => ({ ...prev, is_following: !prev.is_following }))}
                      >
                        {formData.is_following ? <HiBell size={16} /> : <HiOutlineBell size={16} />}
                        Seguir
                      </button>
                      <div className="create-stock-option-text">
                        <span className="create-stock-option-label">Seguir este producto</span>
                        <span className="create-stock-option-desc">
                          Monitoreo activado, cada vez que se consuma, se enviará una notificación a los administradores
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Favorite Option */}
                  <div className="create-stock-option-row">
                    <div className="create-stock-option-control">
                      <button
                        type="button"
                        className={`mp-form-chip ${formData.is_favorite ? 'selected' : ''}`}
                        onClick={() => setFormData((prev) => ({ ...prev, is_favorite: !prev.is_favorite }))}
                      >
                        {formData.is_favorite ? <HiHeart size={16} /> : <HiOutlineHeart size={16} />}
                        Favorito
                      </button>
                      <div className="create-stock-option-text">
                        <span className="create-stock-option-label">Marcar como favorito</span>
                        <span className="create-stock-option-desc">
                          Aparecerá siempre en la parte superior de la lista de stock
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="mp-modal-footer">
        <button className="mp-modal-btn-cancel" onClick={onClose}>
          Cancelar
        </button>
        <button
          className="mp-modal-btn-submit"
          onClick={handleSave}
          disabled={
            !selectedEntityId || 
            !formData.storage_unit_id || 
            !formData.unidad_medida_id || 
            formData.system_stock === '' || 
            formData.min_quantity === '' || 
            existingStock || 
            checkingDuplicate ||
            loading
          }
        >
          Guardar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateStockModal;